<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { useSteps } from '/@src/composable/useSteps'
import { toEuro } from '/@src/utils/numbers'
import { NazvyKrokov } from '/@src/utils/zoppm/types'
import { inject, Ref } from 'vue'
import { ProjektFinancovanieSposobFinancovaniaEnum } from '/@src/client-portal'
import { ZoppmSchemeTransformer } from '/@src/utils/zoppm'
import { NazvyAssemblySekcii } from '/@src/utils/zoppm/ZoppmSchemeTransformer/types'
import { computed } from 'vue-demi'

export interface ZdrojeFinancovania {
  financovanie: any
  isValidSection: boolean
}

const props = withDefaults(defineProps<ZdrojeFinancovania>(), {
  financovanie: undefined,
  isValidSection: false,
})

const stavPodana = inject<boolean>('stavPodana', false)

const { t } = useI18n()
const steps = useSteps()

const label = {
  [ProjektFinancovanieSposobFinancovaniaEnum['SystemPredfinancovania']]: t(
    'zoppm.step11.part1.radioOpt1'
  ),
  [ProjektFinancovanieSposobFinancovaniaEnum['SystemZalohovyvhPlatieb']]: t(
    'zoppm.step11.part1.radioOpt2'
  ),
  [ProjektFinancovanieSposobFinancovaniaEnum['SystemRefundacie']]: t(
    'zoppm.step11.part1.radioOpt3'
  ),
  [ProjektFinancovanieSposobFinancovaniaEnum['KombinovanySystem']]: t(
    'zoppm.step11.part1.radioOpt4'
  ),
}

const schemeTransformer = inject<Ref<ZoppmSchemeTransformer>>('schemeTransformer')
const sekciaRefNazov: NazvyAssemblySekcii = 'zoppm.ks.financovanie'
const heading = computed(
  () =>
    schemeTransformer?.value.getSectionTitle(sekciaRefNazov) ||
    t('zoppm.zhrnutie.zdrojeFinancovania.mainHeading')
)

const showZdrojeFinancovania = computed(() =>
  schemeTransformer?.value.isSectionEnabled('zoppm.ks.financovanie')
)

const sposobFinancovaniaHeading = computed(
  () =>
    schemeTransformer?.value.getSectionTitle('zoppm.ss.financovanie.sposob') ||
    t('zoppm.zhrnutie.zdrojeFinancovania.mainHeading')
)

const pomocZInychZdrojovHeading = computed(
  () =>
    schemeTransformer?.value.getSectionTitle('zoppm.ss.financovanie.pomoc') ||
    t('zoppm.zhrnutie.zdrojeFinancovania.pomocZInychZdrojov')
)
</script>

<template>
  <IDSKHeading v-if="showZdrojeFinancovania" as="h2" variant="l" class="mb-7">
    {{ heading }}
  </IDSKHeading>

  <div v-if="props.isValidSection">
    <zhrnutie-error />
  </div>

  <template v-if="financovanie?.sposobFinancovania">
    <IDSKHeading as="h3" variant="m">{{ sposobFinancovaniaHeading }}</IDSKHeading>
    <div v-for="sposob in financovanie?.sposobFinancovania" :key="sposob.id">
      <IDSKParagraph class="capitalize-first-letter">{{ label[sposob] }}</IDSKParagraph>
    </div>
    <hr
      v-if="
        financovanie?.statnaPomoc?.schemyStatnejPomoci?.length > 0 ||
        financovanie?.ineZdrojeFinancovania?.length > 0
      "
      class="mt-6 mb-6"
    />
  </template>

  <template v-if="financovanie?.statnaPomoc?.schemyStatnejPomoci?.length > 0">
    <dl class="govuk-summary-list govuk-summary-list--no-border mb-5">
      <div class="govuk-summary-list__row">
        <dt class="govuk-summary-list__key govuk-heading-m">
          {{ t('zoppm.zhrnutie.zdrojeFinancovania.StatnaPomocAleboDeMinimis') }}
        </dt>
        <dd class="govuk-summary-list__value">
          {{ t('globalne.ano') }}
        </dd>
      </div>
      <div class="govuk-summary-list__row">
        <dt class="govuk-summary-list__key">
          {{ t('zoppm.zhrnutie.zdrojeFinancovania.nazovACisloSchemy') }}
        </dt>
        <dd class="govuk-summary-list__value">
          <p
            v-for="schema in financovanie?.statnaPomoc?.schemyStatnejPomoci"
            :key="schema"
          >
            {{ schema?.nazov }}
          </p>
        </dd>
      </div>
      <div class="govuk-summary-list__row">
        <dt class="govuk-summary-list__key">
          {{ t('zoppm.zhrnutie.zdrojeFinancovania.celkovaVyskaStatnejPomoci') }}
        </dt>
        <dd class="govuk-summary-list__value">
          <b>{{ toEuro(financovanie?.statnaPomoc?.vyskaStatnejPomoci?.sumaEur) }}</b>
        </dd>
      </div>
    </dl>
    <hr v-if="financovanie?.ineZdrojeFinancovania?.length > 0" class="mt-6 mb-6" />
  </template>

  <template v-if="financovanie?.ineZdrojeFinancovania?.length > 0">
    <dl class="govuk-summary-list govuk-summary-list--no-border mb-0">
      <div class="govuk-summary-list__row">
        <dt class="govuk-summary-list__key govuk-heading-m">
          {{ pomocZInychZdrojovHeading }}
        </dt>
        <dd class="govuk-summary-list__value">
          {{ t('globalne.ano') }}
        </dd>
      </div>
    </dl>

    <dl
      v-for="ineZdroje in financovanie?.ineZdrojeFinancovania"
      :key="ineZdroje"
      class="govuk-summary-list govuk-summary-list--no-border"
    >
      <div class="govuk-summary-list__row">
        <dt class="govuk-summary-list__key">
          {{ t('zoppm.zhrnutie.zdrojeFinancovania.poskytovatelPomoci') }}
        </dt>
        <dd class="govuk-summary-list__value">
          {{ ineZdroje?.poskytovatelPomoci }}
        </dd>
      </div>
      <div class="govuk-summary-list__row">
        <dt class="govuk-summary-list__key">
          {{ t('zoppm.zhrnutie.zdrojeFinancovania.nazovPomoci') }}
        </dt>
        <dd class="govuk-summary-list__value">
          {{ ineZdroje?.nazovPomoci }}
        </dd>
      </div>
      <div class="govuk-summary-list__row">
        <dt class="govuk-summary-list__key">
          {{ t('zoppm.zhrnutie.zdrojeFinancovania.rokSchvaleniaPomoci') }}
        </dt>
        <dd class="govuk-summary-list__value">
          {{ ineZdroje?.rokSchvaleniaPomoci }}
        </dd>
      </div>
      <div class="govuk-summary-list__row">
        <dt class="govuk-summary-list__key">
          {{ t('zoppm.zhrnutie.zdrojeFinancovania.cisloZmluvy') }}
        </dt>
        <dd class="govuk-summary-list__value">
          {{ ineZdroje?.cisloZmluvy }}
        </dd>
      </div>
      <div class="govuk-summary-list__row">
        <dt class="govuk-summary-list__key">
          {{ t('zoppm.zhrnutie.zdrojeFinancovania.webovaStrankaPoskytovatela') }}
        </dt>
        <dd class="govuk-summary-list__value">
          {{ ineZdroje?.poskytovatelWeb }}
        </dd>
      </div>
      <div class="govuk-summary-list__row">
        <dt class="govuk-summary-list__key">
          {{ t('zoppm.zhrnutie.zdrojeFinancovania.sumaEur') }}
        </dt>
        <dd class="govuk-summary-list__value">
          {{ ineZdroje?.vyskaPoskytnutychProstriedkov?.sumaEur }}
        </dd>
      </div>
      <div
        v-if="ineZdroje?.vyskaProstriedkovSucastouZoPPM"
        class="govuk-summary-list__row"
      >
        <dt class="govuk-summary-list__key">
          {{ t('zoppm.zhrnutie.zdrojeFinancovania.cestneVyhlasenie') }}
        </dt>
        <dd class="govuk-summary-list__value">
          {{ t('globalne.ano') }}
        </dd>
      </div>
    </dl>
  </template>

  <div v-if="!stavPodana" class="d-flex align-items-right">
    <IDSKButton
      variant="secondary"
      type="button"
      class="mb-0"
      :data-test-id="`zoppm-zdroje-financovania-edit`"
      @click="
        () =>
          steps.setStep(NazvyKrokov['zdrojeFinancovaniaProjektu'], {
            ref: NazvyKrokov['zhrnutie'],
          })
      "
    >
      {{ t('zoppm.zhrnutie.editBtn') }}
    </IDSKButton>
  </div>

  <hr class="mb-8 mt-8" />
</template>

<style scoped lang="scss">
@media screen and (min-width: 768px) {
  dl dt {
    width: 50%;
  }
}

.capitalize-first-letter:first-letter {
  text-transform: uppercase;
}
</style>
